<template>
  <tr
    :class="[
      'tm-table-header',
      {
        'tm-table-header--disabled': disabled,
        'tm-table-header--new-sizing': isNewSizingFlowEnabled,
      },
    ]"
  >
    <th
      v-if="shouldShowCheckbox"
      ref="checkboxNode"
      class="tm-checkbox"
    >
      <lazy>
        <tm-table-header-checkbox
          v-if="!singleSelection"
          :service-id="serviceId"
          :disabled="disabled"
        />
      </lazy>
    </th>

    <template
      v-for="column in visibleColumns"
      :key="`${column.columnName}:${column.columnOrder}:${visibleColumns.length}:${getSortDirection(column)}`"
    >
      <template v-if="isSortable(column)">
        <tm-table-header-sort-item
          :label="column.hideHeaderName ? '' : getColumnLabel(column)"
          :direction="getSortDirection(column)"
          :style="getColumnStyles(column)"
          @on-sort-changed="onSortChanged(column)"
        />
      </template>
      <template v-else>
        <th
          :class="[
            column.columnClass,
            {
              'tm-table-header__column--sticky': isStickyColumn(column),
              'tm-table-header__column--sticky-shadow': isStickyColumn(column),
            },
          ]"
          :style="getColumnStyles(column)"
        >
          <span v-if="!column.hideHeaderName">
            {{ getColumnLabel(column) }}
          </span>
        </th>
      </template>
    </template>
    <template v-if="hasActions">
      <lazy>
        <tm-sticky-cell
          tag="th"
          :with-shadow="!hasOtherStickyCells"
          :style="{
            width: addPxToNumber(actionsWidth),
            maxWidth: addPxToNumber(actionsWidth),
            minWidth: addPxToNumber(actionsWidth),
          }"
        />
      </lazy>
    </template>
  </tr>
</template>

<script setup lang="ts">
import { computed, ref, toRef, useResizeObserver, useTemplateRef } from '@/composition/vue/compositionApi'
import Lazy from '@/components/shared/Lazy.vue'
import TmTableHeaderSortItem from '@/components/shared/tables/header/TmTableHeaderSortItem.vue'
import { normalizeStickyCell, type DefaultColumn, type SortDirection } from '@/core/tables/types'
import { useBreakpoints } from '@/composition/breakpoints'
import type { BaseTableInterface } from '@/core/tables/baseTableInterface'
import type Sorter from '@/data/models/tables/Sorter'
import { get, getTranslateService } from '@/core/container/ioc'
import type { RegisteredServices } from '@/core/container/types'
import type { TranslationKey } from '@/services/types'
import { addPercentageToNumber } from '@/utils/string/addPercentageToNumber'
import { addPxToNumber } from '@/utils/string/addPxToNumber'
import TmStickyCell from '@/components/shared/tables/cells/TmStickyCell.vue'
import TmTableHeaderCheckbox from '@/components/shared/tables/TmTableHeaderCheckbox.vue'

const COL_CONTRACT_STEP_SIZE = 5 // px
const COL_W_FALLBACK_VALUE = 120 // px
const COL_MAX_W_FALLBACK_VALUE = 200 // px

const props = withDefaults(
  defineProps<{
    serviceId: RegisteredServices
    visibleColumns: DefaultColumn[]
    hasActions: boolean
    actionsWidth?: number
    withScroll?: boolean
    hideCheckbox?: boolean
    singleSelection?: boolean
    disabled?: boolean
    isNewSizingFlowEnabled?: boolean
    containerEl?: HTMLElement
    hasOtherStickyCells: boolean
    columnsLabelsOverride?: Record<string, string>
  }>(),
  {
    actionsWidth: 52,
    withScroll: false,
    hideCheckbox: false,
    singleSelection: false,
    disabled: false,
  },
)

const baseTable: BaseTableInterface = get(props.serviceId)
const translateService = getTranslateService()
const { activeBreakpoints } = useBreakpoints()

const getColumnLabel = (column: DefaultColumn): string => {
  const override = props.columnsLabelsOverride?.[column.columnName]
  if (override !== undefined) {
    return override
  }

  return column.isCustomFieldColumn
    ? column.label
    : translateService.t((column.labelShort ?? column.label) as TranslationKey)
}
const isDesktop = computed(() => activeBreakpoints.value.includes('sm-x-min'))

const isStickyColumn = (column: DefaultColumn) => {
  const sticky = normalizeStickyCell(column.isStickyCell)
  return isDesktop.value ? sticky.desktop : sticky.mobile
}

const shouldShowCheckbox = computed(() => !props.hideCheckbox && props.visibleColumns.length > 0)

const containerWidth = ref<number>(-1)
useResizeObserver(toRef(props, 'containerEl'), (entries) => {
  if (!props.isNewSizingFlowEnabled) {
    return
  }

  if (entries.length !== 0) {
    containerWidth.value = entries[0].contentRect.width
  }
})

const availableWidthForColumns = computed(
  () => containerWidth.value - checkboxNodeWidth.value - (props.hasActions ? props.actionsWidth : 0),
)
const columnWidths = computed<Record<string, number>>(() => {
  const widths: Record<string, number> = {}

  const totalMaxWidth = props.visibleColumns.reduce((acc, column) => {
    if (column.maxWidth) {
      acc += column.maxWidth
    } else {
      acc += COL_W_FALLBACK_VALUE
    }
    return acc
  }, 0)

  const totalMinWidth = props.visibleColumns.reduce((acc, column) => {
    if (column.minWidth) {
      acc += column.minWidth
    } else {
      acc += COL_W_FALLBACK_VALUE
    }
    return acc
  }, 0)

  const extraSpace = availableWidthForColumns.value - totalMaxWidth
  if (totalMaxWidth < availableWidthForColumns.value && totalMinWidth > availableWidthForColumns.value) {
    /**
     * Case #1:
     *
     * If the total column width with min-width is greater than the available width, set all columns to their min-width
     */
    props.visibleColumns.forEach((column) => {
      widths[column.columnName] = column.minWidth ?? COL_W_FALLBACK_VALUE
    })
  } else if (extraSpace > 0) {
    /**
     * Case #2:
     * All columns can be expanded to its max-width,
     * so expanding it and fill out the extra vertical space expanding the last column if all the columns has
     * the max-width set or multiple last columns (regarding growRatio) if all of them has not max-width
     */

    props.visibleColumns.forEach((column) => {
      widths[column.columnName] = column.maxWidth ?? COL_W_FALLBACK_VALUE
    })
  } else {
    /**
     * Case #3:
     * Columns can't be expanded to its max-width, so trying to contract the columns with step size = COL_CONTRACT_STEP_SIZE
     * until it reaches the min-width
     */
    const MAX_ITERATIONS = 30
    let i = 1
    let totalColWidth = Number.MAX_SAFE_INTEGER
    while (i <= MAX_ITERATIONS && totalColWidth > availableWidthForColumns.value && totalColWidth > totalMinWidth) {
      // eslint-disable-next-line @typescript-eslint/no-loop-func
      totalColWidth = props.visibleColumns.reduce((acc, column) => {
        widths[column.columnName] = column.maxWidth
          ? Math.max(column.maxWidth - i * COL_CONTRACT_STEP_SIZE, column.minWidth ?? COL_W_FALLBACK_VALUE)
          : (column.minWidth ?? COL_W_FALLBACK_VALUE)
        acc += widths[column.columnName]
        return acc
      }, 0)

      i += 1
    }
  }

  const staticColumns = props.visibleColumns.filter((column) => !!column.maxWidth)
  const dynamicColumns = props.visibleColumns.filter((column) => !column.maxWidth)
  const isLastColumnDynamic = dynamicColumns.length > 0 && typeof props.visibleColumns.at(-1)?.maxWidth === 'undefined'

  const staticColumnsWidth = staticColumns.reduce((acc, column) => {
    acc += widths[column.columnName]
    return acc
  }, 0)

  if (dynamicColumns.length) {
    const availableSpaceForDynamicColumns = availableWidthForColumns.value - staticColumnsWidth
    const dynamicColumnsTotalGrowRatio = dynamicColumns.reduce((acc, column) => {
      acc += column.growRatio ?? 1
      return acc
    }, 0)

    dynamicColumns.forEach((column) => {
      const columnWidth = isLastColumnDynamic
        ? (availableSpaceForDynamicColumns / dynamicColumnsTotalGrowRatio) * (column.growRatio ?? 1)
        : COL_MAX_W_FALLBACK_VALUE
      widths[column.columnName] = Math.max(columnWidth, column.minWidth ?? 0)
    })

    if (!isLastColumnDynamic) {
      // expand last column
      const lastColumn = props.visibleColumns.at(-1)
      if (lastColumn) {
        widths[lastColumn.columnName] =
          availableWidthForColumns.value - (staticColumnsWidth - widths[lastColumn.columnName])
      }
    }
  } else {
    // expand last column
    const lastColumn = props.visibleColumns.at(-1)
    if (lastColumn) {
      widths[lastColumn.columnName] =
        availableWidthForColumns.value - (staticColumnsWidth - widths[lastColumn.columnName])
    }
  }

  return widths
})

const checkboxNode = useTemplateRef<HTMLTableCellElement>('checkboxNode')
const checkboxNodeWidth = computed(() => checkboxNode.value?.getBoundingClientRect().width ?? 0)

const sorters = computed(() =>
  Object.fromEntries(
    baseTable
      .getSorters()
      .getAllSorters()
      .map((sorter) => [sorter.name, sorter]),
  ),
)
const totalColumnGrowRatio = computed(() =>
  props.visibleColumns.reduce((acc, curr) => {
    acc += curr.growRatio!
    return acc
  }, 0),
)
const defaultCellWidth = computed(() => 100 / totalColumnGrowRatio.value)
const getSorterByColumnName = (column: DefaultColumn): Sorter => sorters.value[column.columnName]

const isSortable = (column: DefaultColumn): boolean => {
  const columnService = baseTable.getColumns()
  if (columnService.hideHeaderSorting?.()) {
    return false
  }
  return !!getSorterByColumnName(column)
}

const getSortDirection = (column: DefaultColumn): SortDirection => {
  const sorter = getSorterByColumnName(column)
  return sorter?.direction || ''
}

const getColumnStyles = (column: DefaultColumn) => {
  if (!props.isNewSizingFlowEnabled) {
    return {
      ...(column.columnStyles ?? {}),
      width:
        column.growRatio === 0
          ? addPxToNumber(column.minWidth!)
          : addPercentageToNumber(defaultCellWidth.value * column.growRatio!),
      minWidth: addPxToNumber(column.minWidth!),
      maxWidth: column.maxWidth && addPxToNumber(column.maxWidth),
    }
  }

  const colWidth = columnWidths.value[column.columnName]
    ? addPxToNumber(columnWidths.value[column.columnName])
    : undefined

  return {
    ...(column.columnStyles ?? {}),
    width: colWidth,
    maxWidth: column.maxWidth
      ? addPxToNumber(Math.max(column.maxWidth, columnWidths.value[column.columnName]))
      : colWidth,
    minWidth: column.minWidth ? addPxToNumber(column.minWidth) : colWidth,
  }
}
const onSortChanged = (column: DefaultColumn) => {
  if (props.disabled) {
    return
  }

  baseTable.applySort(column.columnName as string)
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.tm-checkbox {
  @include table-checkbox();
}

.tm-table-header {
  &--disabled {
    --tm-table-header-text-color: #{$gray5};
    --tm-table-sort-icon-opacity: 0.5;
    pointer-events: none;
  }
  &__column {
    &--sticky {
      position: sticky !important;
      background-color: var(--tm-table-header-bg, $shade);
      min-width: var(--tm-table-sticky-column-width, unset);
      width: var(--tm-table-sticky-column-width, unset);
    }
    &--sticky-shadow {
      @include table-sticky-cell-shadow;
    }
  }
}
</style>
